import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del } from "common/helpers/HTTP";

import type { ListLeadsTotals } from "../types";

async function removeLeadRequest(
  workspaceId: string,
  listId: string,
  urn: string,
  shouldBlacklist: boolean,
  abortSignal?: AbortSignal,
) {
  const queryParams = new URLSearchParams({
    exclude_targets: "true",
    blacklist_targets: shouldBlacklist.toString(),
  });

  await del(
    `workspaces/${workspaceId}/lists/${listId}/leads/${urn}?${queryParams.toString()}`,
    abortSignal,
  );
}

export default function useIncludedListLeadsMutations(listId: string) {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys, blacklistKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: removeListLead, isPending: isRemovingListLead } =
    useMutation({
      //one lead per call, the bulk actions run these in sequence
      mutationFn: ({
        urn,
        shouldBlacklist,
        abortSignal,
      }: {
        urn: string;
        shouldBlacklist: boolean;
        abortSignal?: AbortSignal;
      }) =>
        removeLeadRequest(
          workspaceId,
          listId,
          urn,
          shouldBlacklist,
          abortSignal,
        ),
      onSuccess: (_, { shouldBlacklist }) => {
        queryClient.setQueryData<ListLeadsTotals>(
          listsKeys.leadsTotals(listId),
          (prevTotals) =>
            prevTotals && {
              included: prevTotals.included - 1,
              excluded: prevTotals.excluded + 1,
            },
        );
        //the lead leaves the pool for the exclusions, so both tabs change
        queryClient.invalidateQueries({
          queryKey: listsKeys.leads(listId, false),
        });
        queryClient.invalidateQueries({
          queryKey: listsKeys.leads(listId, true),
        });
        if (shouldBlacklist) {
          queryClient.invalidateQueries({
            queryKey: blacklistKeys.list("accounts"),
          });
        }
      },
    });

  return { removeListLead, isRemovingListLead };
}
